// =============================================================================
// AuthShell - shared layout + primitives for the public auth pages
// (login, forgot password, activate account).
// Design: Sarsen "Strata" identity (2026-09) — mirrors www.sarsencap.com:
// off-white drafting grid, navy panel with the four-slab mark, one terracotta
// accent per surface, rectilinear controls, Archivo / Archivo Expanded.
// Presentation only: no state, no services, no navigation logic.
// =============================================================================

import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';

// -----------------------------------------------------------------------------
// The mark: four stacked slabs. Geometry is fixed by the brand kit; never re-space.
// -----------------------------------------------------------------------------

const SLAB_GEOMETRY = [
  { left: '10%', top: '74%', width: '80%', height: '18%' },
  { left: '18%', top: '52%', width: '60%', height: '18%' },
  { left: '28%', top: '30%', width: '48%', height: '18%' },
  { left: '34%', top: '8%', width: '30%', height: '18%' },
];

const SLAB_COLORS = ['bg-brand-navy', 'bg-brand-slate', 'bg-brand-stone', 'bg-brand-terracotta'];
const SLAB_COLORS_REVERSE = ['bg-white', 'bg-brand-pale', 'bg-brand-paleslate', 'bg-brand-terracotta'];

interface BrandMarkProps {
  reverse?: boolean;
  animate?: boolean;
  className?: string;
}

export function BrandMark({ reverse = false, animate = false, className = '' }: BrandMarkProps) {
  const colors = reverse ? SLAB_COLORS_REVERSE : SLAB_COLORS;
  return (
    <div className={`relative aspect-square ${className}`} aria-hidden="true">
      {SLAB_GEOMETRY.map((geometry, i) => (
        <div
          key={i}
          className={`absolute ${colors[i]} ${animate ? 'motion-safe:animate-slab-in' : ''}`}
          style={{ ...geometry, animationDelay: animate ? `${0.2 + i * 0.22}s` : undefined }}
        />
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Shell
// -----------------------------------------------------------------------------

interface AuthShellProps {
  children: ReactNode;
  /** Small uppercase caption in the navy panel, e.g. "Investor Portal" */
  caption?: string;
}

export function AuthShell({ children, caption = 'Investor Portal' }: AuthShellProps) {
  const year = new Date().getFullYear();

  return (
    <div className="min-h-screen flex font-brand text-brand-ink bg-brand-offwhite antialiased">
      {/* Left column: lockup, form, footer */}
      <div className="relative w-full lg:w-1/2 flex flex-col auth-grid">
        <header className="px-8 py-7 sm:px-12">
          <Link
            to="/login"
            className="inline-flex items-center gap-3 text-brand-navy"
            aria-label="Sarsen Capital Partners — Investor Portal"
          >
            <BrandMark className="w-10 shrink-0" />
            <span className="flex flex-col items-start gap-1">
              <span className="font-brand-display font-bold text-[20px] leading-none tracking-[.12em] uppercase">
                Sarsen
              </span>
              <span className="text-[8px] font-medium leading-none tracking-[.3em] uppercase whitespace-nowrap">
                Capital Partners
              </span>
            </span>
          </Link>
        </header>

        <main className="flex-1 flex items-center justify-center px-8 sm:px-12 pb-12">
          <div className="w-full max-w-md motion-safe:animate-rise-in">{children}</div>
        </main>

        <footer className="px-8 sm:px-12 py-6 flex flex-wrap items-center justify-between gap-3 text-[11px] tracking-[.06em] text-brand-muted">
          <p className="m-0">&copy; {year} Sarsen Capital Partners LLC. All rights reserved.</p>
          <a
            href="https://www.sarsencap.com"
            className="uppercase tracking-[.2em] hover:text-brand-navy transition-colors"
          >
            sarsencap.com
          </a>
        </footer>
      </div>

      {/* Right column: navy panel with the mark (desktop only) */}
      <aside className="hidden lg:flex lg:w-1/2 relative bg-brand-navy overflow-hidden items-center justify-center">
        <div className="absolute inset-0 auth-grid-dark pointer-events-none" />
        <BrandMark reverse animate className="w-[34%] drop-shadow-[0_24px_28px_rgba(0,0,0,.45)]" />
        <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-6 px-8 py-7 text-[11px] tracking-[.24em] uppercase text-brand-pale pointer-events-none">
          <span>{caption}</span>
          <span className="block w-6 h-0.5 bg-brand-terracotta" />
          <span>Sarsen Capital Partners</span>
        </div>
      </aside>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Primitives shared by the auth pages
// -----------------------------------------------------------------------------

/** Rectilinear text input. Append extra classes (e.g. `pr-12`) as needed. */
export const authInputClass =
  'w-full px-4 py-3.5 bg-white border border-brand-line text-brand-navy placeholder:text-brand-stone ' +
  'focus:outline-none focus:border-brand-navy focus-visible:ring-0 focus-visible:ring-offset-0 transition-colors';

/** Icon-button inside an input (show/hide password) */
export const authInputIconButtonClass =
  'absolute inset-y-0 right-0 pr-4 flex items-center text-brand-stone hover:text-brand-navy transition-colors';

/** Quiet text action (resend code, back) */
export const authQuietButtonClass =
  'text-sm text-brand-navy hover:text-brand-terracotta transition-colors ' +
  'disabled:text-brand-stone disabled:cursor-not-allowed disabled:hover:text-brand-stone';

interface AuthHeadingProps {
  eyebrow?: string;
  title: string;
  subtitle?: ReactNode;
  center?: boolean;
}

export function AuthHeading({ eyebrow, title, subtitle, center = false }: AuthHeadingProps) {
  return (
    <div className={`mb-8 ${center ? 'text-center' : ''}`}>
      {eyebrow && (
        <p className="m-0 mb-4 text-[11px] font-semibold tracking-[.28em] uppercase text-brand-muted">
          {eyebrow}
        </p>
      )}
      <h1 className="m-0 text-[32px] leading-[1.1] font-medium tracking-[-.02em] text-brand-navy">
        {title}
      </h1>
      {subtitle && <p className="m-0 mt-3 text-base text-brand-ink">{subtitle}</p>}
    </div>
  );
}

export function AuthError({ message, center = false }: { message: string; center?: boolean }) {
  return (
    <div className="border-l-2 border-brand-terracotta bg-white px-4 py-3" role="alert">
      <p className={`m-0 text-sm text-brand-navy ${center ? 'text-center' : ''}`}>{message}</p>
    </div>
  );
}

export function AuthNotice({ message }: { message: string }) {
  return (
    <div className="mb-6 border-l-2 border-brand-stone bg-white px-4 py-3">
      <p className="m-0 text-sm text-brand-ink">{message}</p>
    </div>
  );
}

export function AuthButton({ className = '', children, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      className={
        'w-full min-h-[48px] px-4 bg-brand-navy text-white text-xs font-medium uppercase tracking-[.14em] ' +
        'hover:bg-brand-slate transition-colors ' +
        'disabled:bg-brand-pale disabled:text-white disabled:cursor-not-allowed ' +
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-navy focus-visible:ring-offset-2 ' +
        className
      }
    >
      {children}
    </button>
  );
}

export function AuthLink({ to, className = '', children }: { to: string; className?: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className={`text-sm text-brand-navy underline underline-offset-4 decoration-brand-line hover:text-brand-terracotta hover:decoration-brand-terracotta transition-colors ${className}`}
    >
      {children}
    </Link>
  );
}

/** Password-rule row: filled terracotta square when met, hollow stone square when not */
export function AuthCheckItem({ met, text }: { met: boolean; text: string }) {
  return (
    <li className={`flex items-center gap-3 ${met ? 'text-brand-navy' : 'text-brand-stone'}`}>
      <span
        className={`block w-2.5 h-2.5 shrink-0 ${met ? 'bg-brand-terracotta' : 'border border-brand-stone'}`}
        aria-hidden="true"
      />
      <span>{text}</span>
      <span className="sr-only">{met ? ' (met)' : ' (not met)'}</span>
    </li>
  );
}

/** Success badge: navy square with a white check */
export function AuthSuccessBadge() {
  return (
    <div className="flex items-center justify-center mb-8">
      <div className="w-16 h-16 bg-brand-navy flex items-center justify-center">
        <svg viewBox="0 0 24 24" className="w-8 h-8 text-white" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M5 12.5l4.5 4.5L19 7.5" strokeLinecap="square" strokeLinejoin="miter" />
        </svg>
      </div>
    </div>
  );
}
